import { useState } from 'react';
import { Link } from 'react-router-dom';
import { getStrings, type Lang } from '../lib/i18n';
import { headFor, athleteFor, athletePath } from '../lib/events';
import { RANKINGS_OFFICIAL, type RankAthlete } from '../content/events/rankings.generated';

function RankAvatar({ name, champion, lang }: { name: string; champion?: boolean; lang: Lang }) {
  const [broken, setBroken] = useState(false);
  const src = headFor(name);
  const prof = athleteFor(name);
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
  const inner = src && !broken ? (
    <img src={src} alt={name} width={44} height={44} loading="lazy" onError={() => setBroken(true)} className="w-full h-full object-cover" />
  ) : (
    <span className="flex items-center justify-center w-full h-full text-xs font-bold text-faint">{initials}</span>
  );
  const cls = `relative shrink-0 w-11 h-11 rounded-full overflow-hidden bg-elevated border ${champion ? 'border-primary/50 ring-2 ring-primary/20' : 'border-line'}`;
  return prof ? (
    <Link to={athletePath(lang, prof.slug)} title={name} className={`${cls} hover:opacity-90 transition`}>{inner}</Link>
  ) : (
    <div className={cls}>{inner}</div>
  );
}

function Ladder({ arm, rows, championLabel, vacantLabel, armLabel, lang }: { arm: 'right' | 'left'; rows: RankAthlete[]; championLabel: string; vacantLabel: string; armLabel: string; lang: Lang }) {
  if (!rows?.length) return null;
  return (
    <div>
      <h3 className="text-ink font-bold text-sm uppercase tracking-wide mb-3">{armLabel}</h3>
      <div className="rounded-2xl border border-line bg-surface/60 divide-y divide-line overflow-hidden">
        {rows.map((r) =>
          r.vacant ? (
            <div key={`${arm}-vacant`} className="flex items-center gap-3 p-3 sm:p-4 bg-elevated/60">
              <span className="w-6 text-center font-extrabold text-faint">{r.rank}</span>
              <div className="shrink-0 w-11 h-11 rounded-full bg-secondary flex items-center justify-center border border-line">
                <span className="text-[9px] font-black tracking-wider text-white/80">{vacantLabel.slice(0, 3).toUpperCase()}</span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-muted font-bold leading-tight truncate">{vacantLabel}</div>
                <div className="text-faint text-xs truncate">{championLabel}</div>
              </div>
            </div>
          ) : (
            <div key={`${arm}-${r.rank}-${r.name}`} className="flex items-center gap-3 p-3 sm:p-4">
              <span className={`w-6 text-center font-extrabold ${r.champion ? 'text-primary' : 'text-faint'}`}>{r.rank}</span>
              <RankAvatar name={r.name} champion={r.champion} lang={lang} />
              <div className="min-w-0 flex-1">
                {(() => {
                  const prof = athleteFor(r.name);
                  return prof ? (
                    <Link to={athletePath(lang, prof.slug)} className="text-ink font-bold leading-tight truncate block hover:text-primary transition">{r.name}</Link>
                  ) : (
                    <div className="text-ink font-bold leading-tight truncate">{r.name}</div>
                  );
                })()}
                <div className="text-faint text-xs truncate">{r.country}</div>
              </div>
              {r.champion && (
                <span className="text-[11px] font-bold text-primary bg-primary/10 border border-primary/30 px-2.5 py-1 rounded-full whitespace-nowrap">
                  {championLabel}
                </span>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
}

export function RankingsSection({ lang }: { lang: Lang }) {
  const t = getStrings(lang).eastVsWest;
  const classes = RANKINGS_OFFICIAL;
  const [idx, setIdx] = useState(0);
  const active = classes[idx];

  return (
    <section className="mt-14">
      <span className="text-primary text-sm font-bold uppercase tracking-widest">{t.rankingsLabel}</span>
      <h2 className="text-3xl md:text-4xl font-extrabold text-ink mt-2 mb-2">{t.rankingsTitle}</h2>
      <p className="text-muted mb-5">{t.rankingsSub}</p>

      {/* Weight-class filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        {classes.map((c, i) => (
          <button
            key={c.className}
            type="button"
            onClick={() => setIdx(i)}
            className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition ${
              i === idx ? 'bg-primary text-on-gold border-primary' : 'bg-surface text-muted border-line hover:border-primary/40'
            }`}
          >
            {c.className}
          </button>
        ))}
      </div>

      <p className="text-faint text-sm mb-4">{active.weight}</p>
      <div className="grid md:grid-cols-2 gap-6">
        <Ladder arm="right" rows={active.arms.right || []} championLabel={t.champion} vacantLabel={t.vacant} armLabel={`${active.className} · ${t.arm.right}`} lang={lang} />
        <Ladder arm="left" rows={active.arms.left || []} championLabel={t.champion} vacantLabel={t.vacant} armLabel={`${active.className} · ${t.arm.left}`} lang={lang} />
      </div>
      <p className="text-faint text-sm mt-3">{t.rankingsFoot}</p>
    </section>
  );
}
